import { useAuth } from '@repo/auth';

import { AccessDenied } from './access-denied';
import { useRouterAdapter } from './router-adapter';

export interface ConnectedAccessDeniedProps {
  /** Custom title for the access denied message */
  title?: string;
  /** Custom message explaining why access is denied */
  message?: string;
  /** Required roles that the user doesn't have */
  requiredRoles?: string[];
  /** Whether to show role information (default: false) */
  showRoleInfo?: boolean;
}

/**
 * Connected Access Denied Component
 *
 * A connected version of AccessDenied that automatically wires up
 * the router (for go back) and auth (for logout and current roles).
 *
 * This eliminates the need for each app to create its own wrapper.
 *
 * @example
 * ```tsx
 * // Basic usage (no role info shown)
 * <ConnectedAccessDenied requiredRoles={['admin']} />
 *
 * // With role information for debugging
 * <ConnectedAccessDenied
 *   requiredRoles={['admin']}
 *   showRoleInfo
 * />
 * ```
 */
export function ConnectedAccessDenied({
  title,
  message,
  requiredRoles,
  showRoleInfo = false,
}: ConnectedAccessDeniedProps) {
  const { back } = useRouterAdapter();
  const { logout, roles } = useAuth();

  return (
    <AccessDenied
      title={title}
      message={message}
      showRoleInfo={showRoleInfo}
      requiredRoles={requiredRoles}
      currentRoles={roles}
      onGoBack={() => back()}
      onLogout={() => logout()}
    />
  );
}
